import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calculator, Shield, Lock, Server, Eye, AlertTriangle, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

const Security = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Tax Helper Pros
              </span>
            </div>
            
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Shield className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Security & Data Protection
            </h1>
          </div>
          <p className="text-lg text-slate-600">
            Your security and privacy are our top priorities
          </p>
        </div>

        {/* Security Promise */}
        <Card className="shadow-xl border-2 border-green-200 bg-green-50 mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-green-800 text-center justify-center">
              <Lock className="w-8 h-8" />
              Our Security Commitment
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-lg font-semibold text-green-800 mb-6">
              Bank-level security with zero data storage. Your sensitive financial information is processed securely and immediately deleted.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Lock className="w-8 h-8 text-white" />
                </div>
                <h4 className="font-semibold mb-2 text-green-800">256-bit Encryption</h4>
                <p className="text-sm text-green-700">All data encrypted in transit and at rest</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Eye className="w-8 h-8 text-white" />
                </div>
                <h4 className="font-semibold mb-2 text-green-800">Zero Storage</h4>
                <p className="text-sm text-green-700">No sensitive data stored on our servers</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Server className="w-8 h-8 text-white" />
                </div>
                <h4 className="font-semibold mb-2 text-green-800">Secure Infrastructure</h4>
                <p className="text-sm text-green-700">AWS enterprise-grade hosting</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-8">
            <div className="prose prose-slate max-w-none" style={{fontFamily: 'Inter, sans-serif'}}>
              
              <h2 className="text-2xl font-bold text-slate-800 mt-0 mb-4">Data Encryption & Transport Security</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <ul className="space-y-2 text-blue-800 mb-0">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                    <strong>TLS 1.3 Encryption:</strong> All data transmitted between your device and our servers uses the latest encryption standards
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                    <strong>AES-256 Encryption:</strong> Any temporary data processing uses military-grade encryption
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                    <strong>Secure Headers:</strong> HTTPS enforcement with security headers to prevent attacks
                  </li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">Zero Data Storage Policy</h2>
              <p className="text-slate-700 mb-4">
                Tax Helper Pros operates on a <strong>"process and delete"</strong> model:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-6">
                <li><strong>Transient Processing:</strong> Your bank statements and financial documents are processed in secure, temporary memory only</li>
                <li><strong>Immediate Deletion:</strong> All uploaded files and processed data are permanently deleted within minutes of completion</li>
                <li><strong>No Backups:</strong> We do not create backups or copies of your sensitive financial information</li>
                <li><strong>No Analytics:</strong> Your financial data is never used for analytics, machine learning, or any secondary purposes</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">Infrastructure Security</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                    <Server className="w-5 h-5 text-blue-600" />
                    Cloud Security
                  </h4>
                  <ul className="text-sm text-slate-700 space-y-1">
                    <li>• AWS enterprise-grade hosting</li>
                    <li>• SOC 2 Type II compliant infrastructure</li>
                    <li>• DDoS protection and monitoring</li>
                    <li>• Automated security patching</li>
                  </ul>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                    <Lock className="w-5 h-5 text-blue-600" />
                    Access Controls
                  </h4>
                  <ul className="text-sm text-slate-700 space-y-1">
                    <li>• Multi-factor authentication</li>
                    <li>• Role-based access control</li>
                    <li>• Regular access audits</li>
                    <li>• Principle of least privilege</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">Application Security</h2>
              <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-6">
                <li><strong>Secure Authentication:</strong> Password hashing using bcrypt with salt</li>
                <li><strong>SQL Injection Prevention:</strong> Parameterized queries and input validation</li>
                <li><strong>CSRF Protection:</strong> Cross-site request forgery tokens on all forms</li>
                <li><strong>XSS Prevention:</strong> Content Security Policy and input sanitization</li>
                <li><strong>Rate Limiting:</strong> Protection against brute force and DoS attacks</li>
                <li><strong>Security Headers:</strong> HSTS, X-Frame-Options, and other protective headers</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">Payment Security</h2>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-green-800 mb-3 font-medium">
                  <Lock className="w-5 h-5 inline mr-2" />
                  PCI DSS Compliant Payment Processing
                </p>
                <ul className="space-y-2 text-green-700">
                  <li>• Stripe handles all payment processing - we never see your payment details</li>
                  <li>• PCI DSS Level 1 compliance through Stripe</li>
                  <li>• Secure tokenization of payment methods</li>
                  <li>• Fraud detection and prevention</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">Monitoring & Incident Response</h2>
              <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-6">
                <li><strong>24/7 Security Monitoring:</strong> Real-time threat detection and alerting</li>
                <li><strong>Vulnerability Scanning:</strong> Regular automated security assessments</li>
                <li><strong>Incident Response Plan:</strong> Documented procedures for security incidents</li>
                <li><strong>Security Logging:</strong> Comprehensive audit trails for security events</li>
                <li><strong>Regular Penetration Testing:</strong> Third-party security assessments</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">Compliance & Standards</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                  <div>
                    <div className="font-semibold text-blue-800">SOC 2 Type II</div>
                    <div className="text-sm text-blue-700">Security & availability controls</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                  <div>
                    <div className="font-semibold text-blue-800">GDPR Compliant</div>
                    <div className="text-sm text-blue-700">European data protection</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                  <div>
                    <div className="font-semibold text-blue-800">ISO 27001</div>
                    <div className="text-sm text-blue-700">Information security management</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                  <div>
                    <div className="font-semibold text-blue-800">CCPA Compliant</div>
                    <div className="text-sm text-blue-700">California privacy rights</div>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">User Security Best Practices</h2>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-yellow-800 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  How You Can Stay Secure
                </h4>
                <ul className="space-y-2 text-yellow-800">
                  <li>• Use a strong, unique password for your Tax Helper Pros account</li>
                  <li>• Keep your browser and operating system updated</li>
                  <li>• Log out of your account when using shared computers</li>
                  <li>• Never share your account credentials with others</li>
                  <li>• Report any suspicious activity immediately</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">Security Transparency</h2>
              <p className="text-slate-700 mb-4">
                We believe in security through transparency. Our security practices include:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-6">
                <li>Regular third-party security audits with public summaries</li>
                <li>Responsible disclosure program for security researchers</li>
                <li>Open communication about security incidents (if any occur)</li>
                <li>Annual security reports available upon request</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">Contact Security Team</h2>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 mb-3 font-medium">
                  <AlertTriangle className="w-5 h-5 inline mr-2" />
                  Report Security Issues
                </p>
                <p className="text-red-800 mb-2">
                  If you discover a security vulnerability or have security concerns, please contact our security team immediately:
                </p>
                <div className="text-red-800 font-mono text-sm bg-red-100 p-2 rounded">
                  security@taxhelperpros.com
                </div>
                <p className="text-red-700 text-sm mt-2">
                  We take all security reports seriously and will respond within 24 hours.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security Guarantee */}
        <Card className="shadow-xl border-2 border-blue-200 bg-blue-50 mt-8">
          <CardContent className="p-6 text-center">
            <h3 className="text-2xl font-bold text-blue-800 mb-4">Security Guarantee</h3>
            <p className="text-blue-700 text-lg mb-4">
              We guarantee that your sensitive financial data is never stored on our servers. 
              All processing happens in secure, encrypted, temporary memory and is immediately deleted upon completion.
            </p>
            <p className="text-blue-600 font-medium">
              Your security is not just our priority - it's our promise.
            </p>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mt-8">
          <Button 
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Back to Home
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate('/auth')}
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            Get Started Securely
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Security;